<script setup lang="ts">
import { computed } from 'vue'
import type { EventMapUnitDetails, EventMapUnitView } from '~/shared-kernel/presentation/event-map/event-map.types'
import { useCurrencyFormatter } from '~/shared-kernel/presentation/composables/useCurrencyFormatter'

const props = defineProps<{
  unit: EventMapUnitView
  details?: EventMapUnitDetails
  position?: { left: string; top: string } | undefined
}>()
const { t, locale } = useI18n()
const { formatMoney } = useCurrencyFormatter()
const attendance = computed(() => props.details?.attendance)
const number = computed(() => new Intl.NumberFormat(locale.value, { maximumFractionDigits: 1 }))
function attendanceRatio(numerator: number, denominator: number | null, percentage: number | null) {
  const base = `${number.value.format(numerator)}/${denominator === null ? '—' : number.value.format(denominator)}`
  const rate = denominator === null
    ? t('eventMap.tooltip.attendance.capacityUnavailable')
    : percentage === null ? t('eventMap.tooltip.attendance.notApplicable') : `${number.value.format(percentage)}%`
  return `${base} (${rate})`
}
const breakdown = computed(() => props.details?.priceBreakdown)
const currency = computed(() => breakdown.value?.currency ?? props.details?.price?.currency)
const basePrice = computed(() => (
  breakdown.value && currency.value ? formatMoney(breakdown.value.base, currency.value) : null
))
const serviceFee = computed(() => (
  breakdown.value && currency.value ? formatMoney(breakdown.value.serviceFee, currency.value) : null
))
const parafiscalTax = computed(() => (
  breakdown.value?.parafiscalTax !== undefined && currency.value
    ? formatMoney(breakdown.value.parafiscalTax, currency.value)
    : null
))
const totalPrice = computed(() => {
  if (breakdown.value && currency.value) return formatMoney(breakdown.value.total, currency.value)
  if (props.details?.price) return formatMoney(props.details.price.amount, props.details.price.currency)
  return null
})
const seatCapacity = computed(() => props.details?.capacity ?? props.unit.capacity)
const configuredName = computed(() => (
  props.unit.unitLabel?.trim()
  || (props.unit.kind === 'seat' ? t('eventMap.tooltip.seat') : t(`eventMap.tooltip.groupKinds.${props.unit.groupKind}`))
))
const unitTitle = computed(() => (
  `${configuredName.value} ${props.unit.kind === 'seat' ? props.unit.number : props.unit.label}`.trim()
))
</script>

<template>
  <aside class="event-map-tooltip" role="tooltip" :style="position">
    <strong>{{ unitTitle }}</strong>
    <p v-if="unit.expandedFromGroup">{{ $t('eventMap.legend.expandedFromGroup') }}</p>
    <p v-if="unit.individualSale">{{ $t('eventMap.legend.individualSale') }}</p>
    <p v-for="reason in details?.occupancyReasons ?? []" :key="reason">{{ $t(`eventMap.tooltip.occupancyReasons.${reason}`) }}</p>
    <dl v-if="attendance" class="event-map-tooltip__attendance">
      <div>
        <dt>{{ $t('eventMap.tooltip.attendance.issued') }}</dt>
        <dd>{{ attendanceRatio(attendance.issuedCount, attendance.capacity, attendance.issuedPercentage) }}</dd>
      </div>
      <div>
        <dt>{{ $t('eventMap.tooltip.attendance.entered') }}</dt>
        <dd>{{ attendanceRatio(attendance.enteredCount, attendance.issuedCount, attendance.entryPercentage) }}</dd>
      </div>
    </dl>
    <dl v-else>
      <div v-if="seatCapacity">
        <dt>{{ $t('eventMap.tooltip.capacity') }}</dt>
        <dd>{{ seatCapacity }}</dd>
      </div>
      <div v-if="details?.availableChairs !== undefined">
        <dt>{{ $t('eventMap.tooltip.availableChairs') }}</dt>
        <dd>{{ details.availableChairs }}</dd>
      </div>
      <div v-if="basePrice">
        <dt>{{ $t('eventMap.tooltip.basePrice') }}</dt>
        <dd>{{ basePrice }}</dd>
      </div>
      <div v-if="serviceFee">
        <dt>{{ $t('eventMap.tooltip.serviceFee') }}</dt>
        <dd>{{ serviceFee }}</dd>
      </div>
      <div v-if="parafiscalTax !== null">
        <dt>{{ $t('eventMap.tooltip.parafiscalTax') }}</dt>
        <dd>{{ parafiscalTax }}</dd>
      </div>
      <div v-if="totalPrice">
        <dt>{{ $t('eventMap.tooltip.total') }}</dt>
        <dd>{{ totalPrice }}</dd>
      </div>
    </dl>
  </aside>
</template>


<style scoped>
.event-map-tooltip {
  position: absolute;
  z-index: 2;
  top: 1rem;
  right: 1rem;
  box-sizing: border-box;
  width: min(17rem, calc(100% - 2rem));
  padding: 1rem 1.1rem;
  border: 1px solid color-mix(in oklch, var(--tq-color-teal) 26%, var(--tq-border));
  border-radius: var(--tq-radius-md);
  background: color-mix(in oklch, var(--tq-bg-elevated) 94%, transparent);
  color: var(--tq-text);
  box-shadow: var(--tq-shadow-tight);
  backdrop-filter: blur(12px);
  pointer-events: none;
}

.event-map-tooltip dl {
  display: grid;
  margin: 0.75rem 0 0;
}

.event-map-tooltip dl > div {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: baseline;
  gap: 0.75rem;
  padding: 0.4rem 0;
}

.event-map-tooltip dl > div + div {
  border-top: 1px solid color-mix(in oklch, var(--tq-border) 65%, transparent);
}

.event-map-tooltip dt {
  color: var(--tq-text-soft);
  font-size: 0.75rem;
}

.event-map-tooltip dd {
  margin: 0;
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
}

.event-map-tooltip__attendance > div {
  grid-template-columns: minmax(0, 1fr);
  gap: 0.25rem;
}

.event-map-tooltip__attendance dd {
  text-align: left;
  white-space: normal;
}
</style>
